import useAPI from '@/hooks/use-api'
import { type Sorting } from '@/hooks/use-sorting'
import { type IPagination } from '@/interface/pagination.interface'
import { scrollToTop } from '@/utils/scroll-to-top'
import { type Query, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

type UseDataFetchProps<T> = {
  endpoint: string
  customQueryKey?: (string | unknown)[]
  queryEnabled?: boolean
  sorting?: Sorting
  pagination: {
    pagination: IPagination
    setPagination: React.Dispatch<React.SetStateAction<IPagination>>
    isPreferenceReady?: boolean
  }
  queryParams?: Record<string, string>
  refetchInterval?: number | ((query: Query<{ data: T[]; pagination: IPagination } | null>) => number | false)
  /** Scroll the page to the top when the add/edit form opens. Disable when the form isn't at the top of the page */
  scrollOnOpen?: boolean
}

export function useDataFetch<T, V = T | null>({
  refetchInterval,
  endpoint,
  customQueryKey,
  queryEnabled = true,
  sorting,
  pagination: { pagination, setPagination, isPreferenceReady = true },
  queryParams,
  scrollOnOpen = true,
}: UseDataFetchProps<T>) {
  const { get } = useAPI<T>()
  const [addOpen, setAddOpen] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [values, setValues] = useState<T[] | null>(null)
  const refetchStartTimeRef = useRef<number | null>(null)
  const [selectedValue, setSelectedValue] = useState<V | null>(null)

  const queryKey = [
    endpoint,
    pagination.searchTerm,
    pagination.currentPage,
    pagination.perPage,
    sorting,
    queryParams,
    ...(customQueryKey ?? []),
  ]

  const querySignature = JSON.stringify({
    endpoint,
    searchTerm: pagination.searchTerm,
    currentPage: pagination.currentPage,
    perPage: pagination.perPage,
    sorting,
    queryParams,
    customQueryKey,
  })

  const { isLoading, data, error, refetch, isRefetching } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await get(endpoint, {
        search: pagination.searchTerm,
        page: pagination.currentPage.toString(),
        perPage: pagination.perPage.toString(),
        sortId: sorting?.id || '',
        desc: sorting?.desc ? 'true' : 'false',
        ...queryParams,
      })
      if (!response?.status) throw new Error(response?.message)
      return response.data
    },
    enabled: queryEnabled && isPreferenceReady,
    refetchInterval,
  })

  useEffect(() => {
    setValues(null)
  }, [querySignature])

  useEffect(() => {
    if (pagination.refreshTable) void refetch()
  }, [pagination.refreshTable])

  useEffect(() => {
    const MIN_DELAY_MS = 1000

    if (isRefetching) {
      refetchStartTimeRef.current = Date.now()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    } else if (refetchStartTimeRef.current !== null) {
      const elapsed = Date.now() - refetchStartTimeRef.current
      const remainingTime = Math.max(0, MIN_DELAY_MS - elapsed)

      if (timeoutRef.current) clearTimeout(timeoutRef.current)

      timeoutRef.current = setTimeout(() => {
        setPagination((prev) => ({ ...prev, refreshTable: false }))
        refetchStartTimeRef.current = null
        timeoutRef.current = null
      }, remainingTime)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isRefetching])

  useEffect(() => {
    if (!data) return

    setValues(data.data)

    setPagination((prev) => {
      const total = data.pagination?.total
      const currentPage = data.pagination?.currentPage
      const totalPages = data.pagination?.totalPages

      if (total === prev.total && currentPage === prev.currentPage && totalPages === prev.totalPages) {
        return prev
      }

      return {
        ...prev,
        total,
        currentPage,
        totalPages,
        refreshTable: false,
      }
    })
  }, [data])

  useEffect(() => {
    if (selectedValue) {
      setAddOpen(true)
      if (scrollOnOpen) {
        setTimeout(() => {
          scrollToTop()
        }, 100)
      }
    }
  }, [selectedValue])

  useEffect(() => {
    if (!addOpen) setSelectedValue(null)
  }, [addOpen])

  useEffect(() => {
    if (addOpen && scrollOnOpen) {
      setTimeout(() => {
        scrollToTop()
      }, 100)
    }
  }, [addOpen])

  return {
    isLoading,
    error,
    values,
    addOpen,
    setAddOpen,
    selectedValue,
    setSelectedValue,
  }
}
